import React, { useEffect, useState } from "react";
import { Col, Button, Form, Row } from "react-bootstrap";
import Select from "react-select"; // for searchable dropdown
import isEmpty from "../../../../utils/is-Empty";
import { getCurrentVehicleLoad } from "../../../../actions/structure.action";
import { useDispatch, useSelector } from "react-redux";
import { useDialog } from "../../../../helpers/useDialog";
import PageSpinner from "../../../common/PageSpinner";

const LogisticAssignSection = ({
  currentOrder,
  availableVehicles,
  assignToLP,
  reassignLP,
  setReassignLP,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [km, setKm] = useState("");
  const [totalPrice, setTotalPrice] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [agreeCost, setAgreeCost] = useState(false);
  const [loading, setLoading] = useState(false);
  const { DialogModal, confirm, alert } = useDialog();
  const [availableLoad, setAvailableLoad] = useState(null);

  const dispatch = useDispatch();

  const { currentVehicleLoad } = useSelector((state) => {
    return state;
  });

  useEffect(() => {
    if (!isEmpty(selectedVehicle) && !isEmpty(currentVehicleLoad)) {
      if (
        currentVehicleLoad?.data?.totalLoad >= 0 &&
        selectedVehicle?.data?.vehicle_payload_capacity > 0
      ) {
        const totalLoad = parseFloat(currentVehicleLoad.data.totalLoad);
        const payloadCapacity = parseFloat(selectedVehicle.data.vehicle_payload_capacity);
        if (totalLoad > payloadCapacity) {
          setAvailableLoad(0);
        } else {
          const available = ((payloadCapacity - totalLoad) / payloadCapacity) * 100;
          setAvailableLoad(available.toFixed(0));
        }
      }
    }
  }, [currentVehicleLoad, selectedVehicle]);

  const getSelectedLPLoad = async (option) => {
    console.log("----> getSelectedLPLoad: ", option);
    // Call API to get current load of selected LP
    setLoading(true);
    const currentLoadRes = await getCurrentVehicleLoad(
      dispatch,
      option.data.id,
      option.data.logistic_vehicle_id
    )
      .finally(() => setLoading(false))
      .catch((err) => {
        console.error("Error fetching current vehicle load: ", err);
        setLoading(false);
      });
    console.log("----> Current Load Response: ", currentLoadRes);
  };

  const handleSelectChange = (option) => {
    console.log("---> SELECTED VEHICLE OPTION: ", option);
    setSelectedVehicle(option);
    setTotalPrice(null);
    setShowCalculator(false);
    setAgreeCost(false);
    if (!isEmpty(option?.data)) {
      getSelectedLPLoad(option);
    }
  };

  const excludedRegNos = currentOrder?.data?.logisticPartnerVehicle
    ? [currentOrder?.data?.logisticPartnerVehicle?.vehicle_reg_no].filter(Boolean)
    : [currentOrder?.data?.logisticPartner?.vehicle_reg_no].filter(Boolean); // remove undefined/null values

  // Prepare options for react-select
  const vehicleOptions = availableVehicles
    .filter((vehicle) => !excludedRegNos.includes(vehicle.vehicle_reg_no))
    .map((vehicle) => ({
      value: vehicle.id,
      label: `${vehicle.driver_name}, ${vehicle.vehicle_reg_no} — Payload: ${vehicle.vehicle_payload_capacity}kg (₹${vehicle.business_info.per_km_rate}/km)`,
      data: vehicle,
    }));

  const handleCalculate = () => {
    console.log("SELECTED: ", selectedVehicle);
    if (km && !isNaN(km)) {
      setTotalPrice(
        (parseFloat(km) * parseFloat(selectedVehicle.data.business_info.per_km_rate)).toFixed(2)
      );
      setShowCalculator(true);
    } else {
      setTotalPrice(null);
      setShowCalculator(false);
      alert("Please enter a valid number");
    }
  };

  useEffect(() => {
    if (!showCalculator) {
      setKm("");
      setTotalPrice(null);
    }
  }, [showCalculator]);

  return (
    <>
      {DialogModal}
      {loading && <PageSpinner />}
      <Col
        xs={12}
        sm={12}
        md={4}
        lg={4}
        className="p-0 d-flex flex-column justify-content-end left-col"
      >
        {!isEmpty(currentOrder?.data) &&
          (currentOrder?.data?.status === "accepted" ||
            currentOrder?.data?.status === "shipped") && (
            <div className="order-main-section d-flex flex-column gap-3">
              {/* ✅ Searchable Dropdown */}
              <div className="m-0">
                <div className="m-0 mb-1">
                  <strong>{"Total Order Weight:"}</strong>{" "}
                  <span className="text-primary fw-bold">
                    {parseFloat(currentOrder?.data?.order_weight).toFixed(0)} kg
                  </span>
                </div>
                <Form.Label className="fw-bold mb-2">Select Logistic Partner</Form.Label>
                <Select
                  options={vehicleOptions}
                  onChange={handleSelectChange}
                  placeholder="Search & select LP..."
                  isClearable
                  className="w-100"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#ced4da",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#80bdff" },
                    }),
                    option: (base, state) => ({
                      ...base,
                      color: state.data.data.isSellerLP ? "#955c00ff" : "black", // ← CONDITIONAL TEXT COLOR
                      backgroundColor: state.isFocused
                        ? "#e6f2ff"
                        : state.isSelected
                        ? "#cce5ff"
                        : "white",
                    }),
                  }}
                />
              </div>

              {/* ✅ Selected Vehicle Details */}
              {selectedVehicle && (
                <div className="p-3 border rounded bg-light">
                  <div className="d-flex justify-content-end">
                    <strong className="me-2">Status:</strong>
                    <span
                      className={`fw-bold d-flex align-items-center ${
                        selectedVehicle?.data?.vehicle_is_busy ? "text-danger" : "text-success"
                      }`}
                    >
                      <span
                        className={`me-1 rounded-circle ${
                          selectedVehicle?.data?.vehicle_is_busy ? "bg-danger" : "bg-success"
                        }`}
                        style={{ width: "10px", height: "10px" }}
                      ></span>
                      {selectedVehicle?.data?.vehicle_is_busy ? "Busy, In Transit" : "Available"}
                    </span>
                  </div>
                  <div className="d-flex justify-content-end">
                    <strong className="me-1">Driver:</strong>
                    <span>
                      {selectedVehicle.data.driver_name}
                      {" | "}
                      {selectedVehicle.data.driver_contact}
                    </span>
                  </div>
                  <div className="d-flex justify-content-end">
                    <strong className="me-1">Reg No:</strong> {selectedVehicle.data.vehicle_reg_no}
                  </div>
                  <div className="d-flex justify-content-end">
                    <strong className="me-1">Max Payload:</strong>{" "}
                    {selectedVehicle.data.vehicle_payload_capacity} kg
                  </div>
                  <div className="d-flex justify-content-end">
                    <strong className="me-1">Current Load:</strong>{" "}
                    <span
                      className={
                        availableLoad !== null && availableLoad <= 0
                          ? "text-danger fw-bold"
                          : "text-success fw-bold"
                      }
                    >
                      {currentVehicleLoad?.data?.totalLoad} kg{" "}
                      {availableLoad !== null && (
                        <span>
                          {availableLoad <= 0 ? "(Overload)" : `(Available: ${availableLoad}%)`}
                        </span>
                      )}
                    </span>
                  </div>
                  <hr className="m-0" />
                  <div className="d-flex justify-content-end">
                    <strong className="me-1">City:</strong>{" "}
                    {selectedVehicle.data.business_info.city}
                    &nbsp;|&nbsp;
                    <strong className="me-1">Rate:</strong> ₹
                    {selectedVehicle.data.business_info.per_km_rate}/km
                  </div>

                  <div className="d-flex justify-content-end">
                    <Row className="m-0 p-0 mt-2 align-items-center justify-content-end">
                      <Col xs="auto" className="m-0 p-0">
                        <Form.Label className="fw-bold mb-0">Enter KM:</Form.Label>
                      </Col>
                      <Col xs="auto">
                        <Form.Control
                          className="rounded"
                          type="number"
                          placeholder="e.g. 10"
                          value={km}
                          onChange={(e) => setKm(e.target.value)}
                          style={{ width: "100px", height: "32px" }}
                        />
                      </Col>
                      <Col xs="auto" className="m-0 p-0">
                        <Button variant="primary" onClick={handleCalculate}>
                          Calculate
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  <div className="d-flex justify-content-end">
                    {totalPrice !== null && (
                      <div className="fw-bold">Total Price: ₹{totalPrice}</div>
                    )}
                  </div>
                  {showCalculator && (
                    <div className="d-flex justify-content-end">
                      {/* Checkbox to toggle calculator visibility */}
                      <Form.Check
                        type="checkbox"
                        label="Agree for total KM price cost for LP"
                        checked={agreeCost}
                        onChange={(e) => setAgreeCost(e.target.checked)}
                        className="fw-bold showKmCalc"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-end">
                {reassignLP && (
                  <div>
                    <Button variant="primary" onClick={() => setReassignLP(false)}>
                      <i className="bi bi-x-lg"></i> Cancel
                    </Button>
                    &nbsp;
                  </div>
                )}

                {/* ✅ Assign Button (Right Aligned) */}
                {totalPrice !== null &&
                  parseFloat(totalPrice) > 0 &&
                  showCalculator &&
                  agreeCost && (
                    <div className="text-end">
                      <Button
                        variant="primary"
                        className="assign-btn"
                        onClick={async () => {
                          // If the vehicle is busy and in transit, get a confirmation
                          if (selectedVehicle && selectedVehicle?.data?.vehicle_is_busy) {
                            const isBusyConfirmed = await confirm(
                              <div className="text-dark">
                                <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
                                <strong>Warning:</strong> The vehicle is busy and in transit. Are
                                you sure you want to proceed?
                              </div>,
                              <>
                                <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
                                Confirmation Required!
                              </>
                            );
                            if (!isBusyConfirmed) {
                              return;
                            }
                          }

                          // Get a confirmation, if the payload exceeds max payload of the vehicle
                          const totalVehicleLoad =
                            parseFloat(currentVehicleLoad?.data?.totalLoad) +
                            parseFloat(currentOrder?.data?.order_weight);
                          if (
                            totalVehicleLoad >
                            parseFloat(selectedVehicle?.data?.vehicle_payload_capacity)
                          ) {
                            const isConfirmed = await confirm(
                              <div className="text-dark">
                                <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
                                <strong>Warning:</strong> Assigning this order will exceed payload
                                capacity. Are you sure you want to proceed?
                              </div>,
                              <>
                                <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
                                Confirm Assignment
                              </>
                            );
                            if (!isConfirmed) {
                              return;
                            }
                          }

                          // Complete the assignment
                          assignToLP(
                            currentOrder?.data?.id,
                            selectedVehicle,
                            totalPrice !== null && parseFloat(totalPrice) > 0
                              ? {
                                  totalPrice: totalPrice,
                                  km: km,
                                }
                              : null
                          );
                        }}
                        disabled={!selectedVehicle}
                      >
                        <i className="bi bi-person-add"></i> Assign LP
                      </Button>
                    </div>
                  )}
              </div>
            </div>
          )}
      </Col>
    </>
  );
};

export default LogisticAssignSection;
